import React, { useMemo } from 'react';
import { Modal, Button, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import ViewPdfButton from './ViewPdfButton';

const badgeVariant = (status) => {
  switch (status) {
    case 'COMPLETED': return 'success';
    default: return 'secondary';
  }
};

export default function ExchangeViewModal({ show, onHide, row, loading = false, error = '' }) {
  const typeLabel = useMemo(() => {
    const t = String(row?.type || '').toUpperCase();
    if (t === 'BUY') return 'Achat USD';
    if (t === 'SELL') return 'Vente USD';
    return '—';
  }, [row]);

  const clientLabel = useMemo(() => {
    if (row?.client) {
      return `${row.client.firstName || ''} ${row.client.lastName || ''}`.trim();
    }
    return row?.clientName || 'Non-client';
  }, [row]);

  const getDocument = () => ({
    title: `Détails Exchange${row?.id ? ` - #${row.id}` : ''}`,
    sections: [
      {
        title: 'Informations générales',
        rows: [
          { label: 'Type', value: typeLabel },
          { label: 'Client / bénéficiaire', value: clientLabel },
          { label: 'Statut', value: row?.status || '—' },
          { label: 'Créée le', value: row?.createdAt ? new Date(row.createdAt).toLocaleString() : '—' },
          { label: 'Créée par', value: row?.createdByUser?.username || '—' },
          { label: 'Modifiée par', value: row?.modifiedByUser?.username || '—' },
          { label: 'Date modification', value: row?.modifiedAt ? new Date(row.modifiedAt).toLocaleString() : '—' },
        ],
      },
      {
        title: 'Montants',
        rows: [
          { label: 'Montant USD', value: `${Number(row?.amountUSD || 0).toFixed(2)} USD` },
          { label: 'Taux utilisé', value: `${Number(row?.rateUsdHtg || 0).toFixed(2)} HTG` },
          { label: 'Montant HTG', value: `${Number(row?.amountHTG || 0).toFixed(2)} HTG` },
        ],
      },
      {
        title: 'Note',
        rows: [
          { label: 'Note', value: row?.note || '—' },
        ],
      },
    ],
  });

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Détails Exchange</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <Alert variant="secondary" className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <div>Chargement…</div>
          </Alert>
        )}

        {!!error && <Alert variant="danger">{error}</Alert>}

        {!loading && !row && !error && <Alert variant="secondary">Aucune donnée à afficher.</Alert>}

        {!!row && (
          <>
            <Alert variant="secondary" className="d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-semibold">{typeLabel}</div>
                <div className="text-muted small">{clientLabel}</div>
              </div>
              <Badge bg={badgeVariant(row.status)}>{row.status || '—'}</Badge>
            </Alert>

            <Row className="g-3">
              <Col md={6}>
                <div className="text-muted small">Client / bénéficiaire</div>
                <div className="fw-semibold">{clientLabel}</div>
              </Col>

              <Col md={6}>
                <div className="text-muted small">Créée le</div>
                <div className="fw-semibold">
                  {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                </div>
              </Col>

              <Col md={6}>
                <div className="text-muted small">Créée par</div>
                <div className="fw-semibold">{row.createdByUser?.username || '—'}</div>
              </Col>

              <Col md={6}>
                <div className="text-muted small">Modifiée par</div>
                <div className="fw-semibold">{row.modifiedByUser?.username || '—'}</div>
                <div className="text-muted small">
                  {row.modifiedAt ? new Date(row.modifiedAt).toLocaleString() : '—'}
                </div>
              </Col>

              <Col md={4}>
                <div className="text-muted small">Montant USD</div>
                <div className="fw-semibold">{Number(row.amountUSD || 0).toFixed(2)} USD</div>
              </Col>

              <Col md={4}>
                <div className="text-muted small">Taux utilisé</div>
                <div className="fw-semibold">{Number(row.rateUsdHtg || 0).toFixed(2)} HTG</div>
              </Col>

              <Col md={4}>
                <div className="text-muted small">Montant HTG</div>
                <div className="fw-semibold">{Number(row.amountHTG || 0).toFixed(2)} HTG</div>
              </Col>

              <Col md={12}>
                <div className="text-muted small">Note</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{row.note || '—'}</div>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!!row && <ViewPdfButton getDocument={getDocument} className="me-2" />}
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
}